import { useEffect, useMemo, useState } from 'react';
import style from '../../../styles/style.module.css';
import { FaArrowLeft } from 'react-icons/fa6';
import {
  Table,
  Avatar,
  TextInput,
  Checkbox,
  LoadingOverlay,
  Loader,
} from '@mantine/core';
import { FaTimes } from 'react-icons/fa';
import {
  apiGetAllArtwork,
  apiGetFeaturedArtwork,
  apiUpdateFeaturedArtwork,
} from '../../../api/ApiServices';
import { notifications } from '@mantine/notifications';

const FeaturedArtwork = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isTableView, setIsTableView] = useState(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [featuredArtworks, setFeaturedArtworks] = useState<string[]>([]);
  const [replacingArtworkId, setReplacingArtworkId] = useState<string | null>(
    null
  );
  const [allArtworks, setAllArtworks] = useState<Work[]>([]);
  const [featArtworks, setFeatArtworks] = useState<Work[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredArtwork, setFilteredArtwork] = useState<Work[]>([]);
  const [currentFeaturedArtworks, setCurrentFeaturedArtworks] = useState<
    string[]
  >([]);
  const allArtwork = async () => {
    setLoading(true);
    try {
      const resp = await apiGetAllArtwork();
      setAllArtworks(resp.works);
      console.log(resp.works);
      setLoading(false);
    } catch (error: any) {
      notifications.show({
        title: 'Something went wrong',
        color: 'red',
        mt: '100px',
        message: error?.message || 'An error occurred, please try again later.',
      });
      setLoading(false);
    }
  };

  const getFeaturedArtwork = async () => {
    setLoading(true);
    try {
      const resp = await apiGetFeaturedArtwork();
      setFeatArtworks(resp.featuredArtworks);
      setCurrentFeaturedArtworks(
        resp.featuredArtworks.map((artwork) => artwork._id)
      );
      setLoading(false);
    } catch (error: any) {
      notifications.show({
        title: 'Something went wrong',
        color: 'red',
        mt: '100px',
        message: error?.message || 'An error occurred, please try again later.',
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    allArtwork();
    getFeaturedArtwork();
    setFeaturedArtworks(allArtworks?.slice(0, 3).map((item) => item._id));
  }, []);

  const handleTableClick = () => {
    setIsTableView(true);
  };
  const handleGoBack = () => {
    setIsTableView(false);
  };

  const handleReplace = (id: string) => {
    setReplacingArtworkId(id);
    setSelectedRows([id]);
    setIsTableView(true);
  };

  const handleReplaceAll = () => {
    setReplacingArtworkId(null);
    setSelectedRows(featArtworks.map((artwork) => artwork._id));
    setIsTableView(true);
  };

  const handleRowSelection = (id: string) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        // If the id is already selected, remove it
        return prev.filter((rowId) => rowId !== id);
      } else {
        // If the id is not selected, add it (up to the limit)
        const limit = replacingArtworkId ? 1 : 3;
        return [...prev, id].slice(0, limit);
      }
    });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      let payload;
      if (featArtworks.length === 0) {
        // If there are no existing featured artworks
        payload = {
          addedFeaturedArtworkIds: selectedRows,
        };
      } else if (replacingArtworkId) {
        // If replacing a single artwork
        payload = {
          addedFeaturedArtworkIds: selectedRows,
          removedFeaturedArtworkIds: [replacingArtworkId],
        };
      } else {
        // If replacing all artworks
        payload = {
          addedFeaturedArtworkIds: selectedRows,
          removedFeaturedArtworkIds: featArtworks.map((artwork) => artwork._id),
        };
      }
      const resp = await apiUpdateFeaturedArtwork(payload);
      notifications.show({
        title: 'Success',
        color: 'green',
        mt: '100px',
        message: 'Featured artworks updated successfully.',
      });
      getFeaturedArtwork();
      setFeaturedArtworks(selectedRows);
      setCurrentFeaturedArtworks(selectedRows); // Update the current featured artworks

      setSelectedRows([]);
      setIsTableView(false);
    } catch (error: any) {
      notifications.show({
        title: 'Something went wrong',
        color: 'red',
        mt: '100px',
        message: error?.message || 'An error occurred, please try again later.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSelectedCards = () => {
    return (
      <div className='flex gap-4 mb-8'>
        {selectedRows?.map((id) => {
          const item = allArtworks.find((d) => d._id === id);
          if (!item) return null;
          return (
            <div
              key={id}
              className='relative w-48 h-68 p-2 bg-white shadow-[0_2px_40px_-1px_rgba(0,0,0,0.1)] rounded-lg overflow-hidden'>
              <img
                src={item.previewImage}
                alt={item.title}
                className='w-full h-36 object-cover rounded-sm'
              />
              <div className='p-2'>
                <h3 className='font-bold '>
                  {item.artist?.firstName} {item.artist?.lastName}
                </h3>{' '}
                <p className='text-xs my-1'>{item.title}</p>
                <p className='text-xs my-1'>{item.categories.join(', ')}</p>
              </div>
              <button
                onClick={() => handleRowSelection(id)}
                className='absolute top-1 right-1 bg-white rounded-full p-1'>
                <FaTimes className='text-red-500' />
              </button>
            </div>
          );
        })}
      </div>
    );
  };
  useEffect(() => {
    let filtered = allArtworks;

    const lowercaseQuery = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(
      (artist) =>
        artist.artist?.firstName.toLowerCase().includes(lowercaseQuery) ||
        artist.artist?.lastName.toLowerCase().includes(lowercaseQuery)
    );
    setFilteredArtwork(filtered);
  }, [searchQuery, allArtworks]);

  const renderTable = () => {
    const rows = filteredArtwork?.map((element) => {
      const isSelected = selectedRows.includes(element._id);
      const isFeatured = currentFeaturedArtworks.includes(element._id);

      return (
        <Table.Tr
          key={element._id}
          className={`cursor-pointer ${isFeatured ? 'featured-artwork' : ''} ${
            isFeatured ? 'bg-[#f1f1f1]' : ''
          }`}>
          <Table.Td>
            <Checkbox
              color='#DA3400'
              aria-label='Select row'
              checked={isSelected}
              onChange={() => handleRowSelection(element._id)}
            />
          </Table.Td>
          <Table.Td>
            <Avatar src={element.previewImage} alt='preview Image Work' />
          </Table.Td>
          <Table.Td>
            {element.artist?.firstName} {element.artist?.lastName}
          </Table.Td>
          <Table.Td>{element.title}</Table.Td>
          <Table.Td>{element.categories.join(', ')}</Table.Td>
          <Table.Td>{element.materials.join(', ')}</Table.Td>
          <Table.Td>
            {element.dimensionLengthInCM}'' x {element.dimensionWidthInCM}''
          </Table.Td>
        </Table.Tr>
      );
    });

    return (
      <section className='border-[1px] shadow-lg p-3 rounded-lg'>
        <div className='flex justify-between mt-4 mb-8'>
          <p className='my-auto'>
            {replacingArtworkId && replacingArtworkId?.length >= 1
              ? 'Select One Artwork'
              : 'Select Three Artworks'}
          </p>
          <div className='flex gap-6'>
            <TextInput
              placeholder='Search by name'
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.currentTarget.value)}
            />
            <button className='bg-[#DA3400] text-white px-10 rounded-lg font-bold'>
              Search
            </button>
          </div>
        </div>
        <Table highlightOnHover>
          <Table.Thead bg='#EFF1F0' py='xl'>
            <Table.Tr>
              <Table.Th />
              <Table.Th />
              <Table.Th className='py-4'>Artist Name</Table.Th>
              <Table.Th>Artwork Title</Table.Th>
              <Table.Th>Artwork Category</Table.Th>
              <Table.Th>Artwork Material</Table.Th>
              <Table.Th>Artwork Dimension</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredArtwork.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={10} style={{ height: '200px' }}>
                  <div className='flex justify-center items-center h-full'>
                    <p className='text-lg font-semibold text-center'>
                      No artists found.
                    </p>
                  </div>
                </Table.Td>
              </Table.Tr>
            ) : (
              rows
            )}
          </Table.Tbody>
        </Table>
      </section>
    );
  };

  return (
    <section className={style.main}>
      {isTableView ? null : (
        <h1 className='font-bold text-lg mt-3 mb-6'>Featured Artworks</h1>
      )}

      {isTableView ? (
        <div className='relative'>
          <LoadingOverlay
            visible={isLoading}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
            loaderProps={{ color: 'red', type: 'bars' }}
          />
          <div className='flex items-center mb-4 mt-3'>
            <button
              className='mr-4 text-xs font-normal flex'
              onClick={handleGoBack}>
              <span className='my-auto ml- mr-2'>
                <FaArrowLeft />
              </span>
              Go Back
            </button>
            <h1 className='font-bold text-lg'>
              <span className='text-sm font-normal'>Featured Artworks / </span>
              {replacingArtworkId
                ? 'Replace Artwork'
                : featArtworks.length > 0
                ? 'Replace All Artworks'
                : 'Add Artworks'}
            </h1>
          </div>

          <div className=' mt-8 mb-20'>
            {/* <AlphabetFilter
              onLetterClick={handleLetterClick}
              activeLetter={activeLetter}
            /> */}

            {renderSelectedCards()}
            {renderTable()}
            <button
              onClick={handleSubmit}
              disabled={isLoading || selectedRows.length === 0}
              className='bg-[#DA3400] text-white py-2 px-12 rounded-lg font-bold mt-10'>
              {isLoading
                ? 'Uploading...'
                : replacingArtworkId
                ? 'Replace Artwork'
                : 'Upload Artworks'}
            </button>
          </div>
        </div>
      ) : loading ? (
        <div className='flex justify-center text-center items-center h-[70vh] my-auto'>
          <Loader size={40} />
        </div>
      ) : featArtworks?.length > 0 ? (
        <>
          <div className='grid grid-cols-3 gap-10 mb-12'>
            {featArtworks?.map((item, index) => (
              <div
                key={index}
                className='mt-1 p-4 bg-white rounded-lg shadow-[0_2px_40px_-1px_rgba(0,0,0,0.1)]'>
                <img
                  src={item.previewImage}
                  className='w-full h-64 object-cover rounded-md'
                  alt={item.title}
                />
                <div className='mt-2'>
                  <h3 className='font-bold'>
                    {item.artist?.firstName} {item.artist?.lastName}
                  </h3>
                  <p className=' my-1'>{item.title}</p>
                  <p className=' my-1'>{item.categories.join(', ')}</p>
                  <p className=''>
                    {item.dimensionLengthInCM}'' x {item.dimensionWidthInCM}''
                  </p>
                </div>
                <div className='flex justify-end'>
                  <button
                    onClick={() => handleReplace(item._id)}
                    className='rounded-md py-1 px-5 bg-[#DA3400] text-white'>
                    {isLoading
                      ? 'Uploading...'
                      : replacingArtworkId
                      ? 'Replace Artwork'
                      : featArtworks.length > 0
                      ? 'Replace'
                      : 'Add Artworks'}
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className='mb-20'>
            <button
              onClick={handleReplaceAll}
              className='rounded-md py-2 px-16 bg-[#DA3400] text-white'>
              Replace All Featured Artworks
            </button>
          </div>
        </>
      ) : (
        <>
          <div className='flex justify-center h-[70vh] my-auto '>
            <div className='my-auto text-center'>
              <p className='font-bold text-xl mb-3'>
                No artwork added to the featured artwork section yet.
              </p>
              <button
                onClick={handleTableClick}
                className='w-10/12 bg-[#DA3400] text-white py-3 rounded-lg font-bold'>
                Add Three Artworks
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default FeaturedArtwork;
